// ShareButton — mints a public, login-free link to a sample's production sheet
// and opens a share sheet (WhatsApp / Email / Copy / native). Reused by the
// detail toolbar (variant="bar") and the list row actions (variant="icon").

import { useEffect, useRef, useState } from 'react'
import { Share2, MessageCircle, Mail, Copy, Check, X } from 'lucide-react'
import { Card } from '../../components/ui/card'
import { createShareLink, sampleShareUrl } from './api'
import { useT } from '../../i18n'
import { useEscapeKey } from '../../lib/useEscapeKey'
import { sampleShareI18n } from '../../i18n/samples/share-button'

export function ShareButton({
  id,
  label,
  variant = 'bar',
}: {
  id: string
  label: string
  variant?: 'bar' | 'icon'
}) {
  const t = useT(sampleShareI18n)
  const [sharing, setSharing] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)

  async function start(e?: React.MouseEvent) {
    e?.stopPropagation()
    setSharing(true)
    try {
      const { token } = await createShareLink(id)
      setShareUrl(sampleShareUrl(token))
    } finally {
      setSharing(false)
    }
  }

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          title={t.share}
          disabled={sharing}
          onClick={start}
          className="grid h-9 w-9 place-items-center rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-indigo-700 disabled:opacity-50"
        >
          <Share2 size={16} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => start()}
          disabled={sharing}
          className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded inline-flex items-center gap-1 hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
        >
          <Share2 size={14} /> <span className="hidden sm:inline">{sharing ? t.preparing : t.share}</span>
        </button>
      )}
      {shareUrl && <ShareModal url={shareUrl} label={label} onClose={() => setShareUrl(null)} />}
    </>
  )
}

function ShareModal({ url, label, onClose }: { url: string; label: string; onClose: () => void }) {
  const t = useT(sampleShareI18n)
  useEscapeKey(true, onClose)
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
    }
  }, [])
  const msg = t.message(label)
  const wa = `https://wa.me/?text=${encodeURIComponent(`${msg}\n${url}`)}`
  const mail = `mailto:?subject=${encodeURIComponent(msg)}&body=${encodeURIComponent(t.mailBody(label, url))}`
  const canNative = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      if (!mountedRef.current) return
      setCopied(true)
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard blocked — the field is selectable as a fallback */
    }
  }

  const tile = 'flex flex-col items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] py-3 text-xs font-medium hover:bg-[var(--color-surface-muted)]'
  return (
    <div role="presentation" className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Card className="w-full max-w-sm p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{t.subtitle}</p>
          </div>
          <button onClick={onClose} className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" aria-label={t.close}><X size={16} /></button>
        </div>

        <div className={`grid ${canNative ? 'grid-cols-4' : 'grid-cols-3'} gap-2`}>
          <a href={wa} target="_blank" rel="noopener noreferrer" className={tile}>
            <MessageCircle size={18} className="text-emerald-600" /> WhatsApp
          </a>
          <a href={mail} className={tile}>
            <Mail size={18} className="text-blue-600" /> {t.email}
          </a>
          <button type="button" onClick={copy} className={tile}>
            {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} className="text-[var(--color-text-muted)]" />}
            {copied ? t.copied : t.copyLink}
          </button>
          {canNative && (
            <button type="button" onClick={() => { void navigator.share({ title: msg, text: msg, url }) }} className={tile}>
              <Share2 size={18} className="text-[var(--color-text-muted)]" /> {t.more}
            </button>
          )}
        </div>

        <input
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="w-full rounded border border-[var(--color-border-strong)] px-2 py-1.5 text-[11px] text-[var(--color-text-muted)]"
        />
      </Card>
    </div>
  )
}
